import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  HStack,
  Icon,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { FaSlack } from "react-icons/fa";
import { FiChevronDown, FiMaximize2 } from "react-icons/fi";
import { LuCompass } from "react-icons/lu";
import { generatePath } from "react-router";
import CaminoMyTasksPreview from "assets/task-resolution-camino-my-tasks-preview.png";
import SlackHomeTabPreview from "assets/task-resolution-slack-home-preview.png";
import CDS from "design-system";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { companyKeys, useCurrentCompanyQuery } from "features/Company/hooks";
import { Company, TaskResolutionSurface } from "models/company";
import apiClient from "services/ApiClient";

interface OptionDef {
  value: TaskResolutionSurface;
  label: string;
  icon: typeof LuCompass;
  previewSrc: string;
  previewCaption: string;
}

const OPTIONS: OptionDef[] = [
  {
    value: "camino_web",
    label: "Camino My Tasks (recommended)",
    icon: LuCompass,
    previewSrc: CaminoMyTasksPreview,
    previewCaption: "My Tasks in the Camino web app",
  },
  {
    value: "slack_home",
    label: "Slack Camino App Home Tab",
    icon: FaSlack,
    previewSrc: SlackHomeTabPreview,
    previewCaption: "Camino's Home tab in Slack",
  },
];

interface PreviewModalButtonProps {
  label: string;
  caption: string;
  icon: typeof LuCompass;
  previewSrc: string;
}

function PreviewModalButton({ label, caption, icon, previewSrc }: PreviewModalButtonProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <>
      <Button
        size="xs"
        variant="ghost"
        leftIcon={<Icon as={icon} boxSize={3.5} />}
        rightIcon={<Icon as={FiMaximize2} boxSize={3} color="content.tertiary" />}
        color="content.secondary"
        fontWeight="normal"
        onClick={onOpen}
        aria-label={`Preview ${label}`}
      >
        {label}
      </Button>
      <Modal isOpen={isOpen} onClose={onClose} size="5xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pr={12}>
            <HStack spacing={2}>
              <Icon as={icon} boxSize={4} color="content.secondary" />
              <Text fontSize="md">{label}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={3}>
              <Image
                src={previewSrc}
                alt={caption}
                w="100%"
                borderRadius="md"
                borderWidth="1px"
                borderColor="gray.200"
              />
              <Text fontSize="sm" color="content.secondary">{caption}</Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

export default function TaskResolutionSettings() {
  const { data: company, isLoading } = useCurrentCompanyQuery();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [pending, setPending] = useState<TaskResolutionSurface | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: (surface: TaskResolutionSurface) =>
      apiClient.put<Company>(generatePath(API_ROUTES.companies.show, { id: "current" }), {
        company: { task_resolution_surface: surface },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: companyKeys.current() });
      setPending(null);
      toast({ title: "Saved", status: "success" });
    },
    onError: () => {
      setPending(null);
      toast({ title: "Error", description: "Could not save preference, please try again.", status: "error" });
    },
  });

  if (isLoading || !company) {
    return (
      <Box>
        <Skeleton h="240px" minWidth="900px" width="calc(100% - 200px)" mt="4" borderRadius="md" />
      </Box>
    );
  }

  const persisted: TaskResolutionSurface = company.taskResolutionSurface ?? "camino_web";
  const selectedOption = OPTIONS.find((o) => o.value === persisted) ?? OPTIONS[0];
  const pendingOption = pending ? OPTIONS.find((o) => o.value === pending) : null;

  return (
    <Box>
      <Card minWidth="900px" width="calc(100% - 200px)" mt="4">
        <CardBody>
          <Flex justifyContent="space-between" w="100%" alignItems="center" p={5}>
            <Box opacity="70%" fontSize="2xl">
              Where should we nudge teams to resolve tasks?
            </Box>
          </Flex>
          <VStack align="stretch" spacing={4} px={10} pb={6}>
            <Text fontSize="sm" color="content.secondary">
              Camino shows tasks in 2 places:
              {" "}
              <Text as="span" fontWeight="semibold" color="content.primary">
                Camino&apos;s
                {" "}
                <Text as="code" px={1} py={0.5} bg="gray.100" borderRadius="sm" fontSize="xs">/my-tasks</Text>
                {" "}
                page
              </Text>
              {" "}
              and the
              {" "}
              <Text as="span" fontWeight="semibold" color="content.primary">Slack Camino App Home Tab</Text>
              .
            </Text>
            <CDS.Callout variant="info" title="We recommend Camino's My Tasks.">
              <Text fontSize="sm">
                It shows every task across every journey on one screen, with filters and the full task drawer.
              </Text>
              <Text fontSize="sm" mt={1}>
                Tasks in the Slack Home Tab are limited by the platform&apos;s design restrictions, and your team is
                less likely to complete them there.
              </Text>
              <Text fontSize="xs" color="content.tertiary" fontStyle="italic" mt={2}>
                After user testing, 8 out of 10 managers preferred Camino&apos;s interface over Slack.
              </Text>
            </CDS.Callout>
              <HStack align="center" spacing={4}>
                <Box flex={1}>
                  <Text fontSize="sm" fontWeight="medium">Resolution surface</Text>
                  <Text fontSize="xs" color="content.secondary">
                    Where teams see and complete their tasks.
                  </Text>
                </Box>
                <Menu matchWidth>
                  <MenuButton
                    as={Button}
                    variant="outline"
                    size="xs"
                    colorScheme="gray"
                    rightIcon={<Icon as={FiChevronDown} boxSize={3} color="gray.500" />}
                    isDisabled={isPending}
                    aria-label="Task resolution surface"
                    minW="220px"
                    maxW="220px"
                    textAlign="left"
                    fontWeight="normal"
                    color="content.primary"
                    px={2}
                    h="28px"
                  >
                    <HStack spacing={1.5}>
                      <Icon as={selectedOption.icon} boxSize={3} color="content.secondary" />
                      <Text fontSize="xs" color="content.primary">{selectedOption.label}</Text>
                    </HStack>
                  </MenuButton>
                  <MenuList shadow="md">
                    {OPTIONS.map((opt) => (
                      <MenuItem
                        key={opt.value}
                        icon={<Icon as={opt.icon} boxSize={4} color="content.secondary" />}
                        onClick={() => { if (opt.value !== persisted) setPending(opt.value); }}
                      >
                        <Text fontSize="sm" fontWeight={persisted === opt.value ? "semibold" : "normal"}>
                          {opt.label}
                        </Text>
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </HStack>

              <AlertDialog
                isOpen={pending !== null}
                leastDestructiveRef={cancelRef}
                onClose={() => setPending(null)}
                isCentered
              >
                <AlertDialogOverlay>
                  <AlertDialogContent>
                    <AlertDialogHeader fontSize="md" fontWeight="semibold">
                      Switch the resolution surface to {pendingOption?.label.replace(" (recommended)", "")}?
                    </AlertDialogHeader>
                    <AlertDialogBody fontSize="sm">
                      Camino will start nudging your team to resolve tasks in{" "}
                      <Text as="span" fontWeight="semibold">
                        {pendingOption?.label.replace(" (recommended)", "")}
                      </Text>
                      . Existing tasks stay where they are — this only affects where new prompts appear.
                    </AlertDialogBody>
                    <AlertDialogFooter>
                      <Button ref={cancelRef} variant="ghost" onClick={() => setPending(null)} isDisabled={isPending}>
                        Cancel
                      </Button>
                      <Button
                        colorScheme="cdsIndigo"
                        ml={3}
                        onClick={() => pending && mutate(pending)}
                        isLoading={isPending}
                      >
                        Switch surface
                      </Button>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialogOverlay>
              </AlertDialog>
              <HStack spacing={2}>
                <Text fontSize="xs" color="content.tertiary">Previews:</Text>
                {OPTIONS.map((opt) => (
                  <PreviewModalButton
                    key={opt.value}
                    label={opt.label.replace(" (recommended)", "")}
                    caption={opt.previewCaption}
                    icon={opt.icon}
                    previewSrc={opt.previewSrc}
                  />
                ))}
              </HStack>
              <Text fontSize="xs" color="content.tertiary">
                Email reminders always link to Camino, since Slack deep links aren&apos;t reliable from every email
                client.
              </Text>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
}
